import {
  Creative,
  CreativeApprovalConfig,
  CreativeBasic,
  CreativeSize,
  OutdoorType
} from 'core/creative/Creative';
import { getCropInfoCreativeSizeText } from 'core/creative/CreativeCropInfo';
import { formatBytes } from 'utils/StringUtil';
import { renderErrors } from './CreativeFormHintRenderFunction';
import { BasicFormProps } from './BasicFormProps';
import { AbstractFormContentModel } from './FormContentModel';
import {
  camelCase,
  compact,
  defaultTo,
  get,
  isEmpty,
  isNil,
  omit,
  omitBy
} from 'lodash';
import i18n from 'i18next';
import JSZip from 'jszip';
import {
  LineMessage,
  LineMessageType,
  LineUserLanguage
} from 'core/advertiser/LineBeacon';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import moment from 'moment';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ScreenDisplayType } from 'core/channelGroup/ChannelGroup';
import { isEpaperChannelGroup } from 'core/channelGroup/EpaperChannelGroup';

import mimeDB from 'mime-db';

export type OutdoorFormProps<T> = {
  readonly model: T;
  readonly hintModalData?: any;
  readonly setHintModalData: (modalData: any) => void;
} & BasicFormProps;

export interface OutdoorFormModel<T> {
  advertiserId: number;
  forPmp: boolean;
  channelName: string;
  channelId: number;
  canChangeBeacon: boolean;
  useMediaDuration: boolean;
  allowEditImageDuration: boolean;
  durationRange?: { min: number; max?: number };
  creativeSizes?: SelectOptions[];
  beaconConnectionOptions?: SelectOptions[];
  beaconTypeOptions: SelectOptions[];
  initBeaconSwitch: boolean;
  neglectedFormFields: string[];
  supportBeacon: boolean;
  supportAudio: boolean;
  fixedCreativeSize: boolean;
  splicedCreativeSize?: `${number} x ${number}`;
  sizeBoxingMap: {
    [key: string]: { width: [number, number]; height: [number, number] };
  };
  init(): Promise<void>;
  getFormContent(): (props: OutdoorFormProps<T>) => JSX.Element;
  getDefaultDuration(newType: OutdoorType, oldDuration: number): number;
  getOutdoorTypeOptions(): SelectOptions[];
  getVideoHints(width: number, height: number): string[];
  getImageHints(width: number, height: number): string[];
  getAudioHints(): string[];
  validateDuration?: (value: number) => string | undefined;
  validateZip: (
    fileData: FileFieldData
  ) => Promise<string | JSX.Element | undefined>;
  validateVideo(
    targetWidth: number,
    targetHeight: number,
    fileData: MediaFieldData
  ): string | JSX.Element | undefined;
  validateAudio(fileData: MediaFieldData): string | JSX.Element | undefined;
  validateImage: {
    (
      fileData: ImageFieldData,
      targetWidth: number,
      targetHeight: number
    ): string | JSX.Element | undefined;
    (
      fileData: ImageFieldData,
      targetWidth?: undefined,
      targetHeight?: undefined
    ): string | JSX.Element | undefined;
  };
  validateDocument: (
    fileData: FileFieldData,
    isExpressPass: boolean
  ) => string | JSX.Element | undefined;
  needSelfCheckDocument(outdoorType: OutdoorType): boolean;
  defaultSelfCheckDocLink(outdoorType: OutdoorType): string | undefined;
  isFileFieldEmpty(
    fileData: FileFieldData | EmptyFileFieldData
  ): fileData is EmptyFileFieldData;
}

export abstract class BasicOutdoorFormModel<T>
  extends AbstractFormContentModel
  implements OutdoorFormModel<T>
{
  public needBannerUrl = false;
  public creativeSizes?: SelectOptions[];
  // 編輯既有拼接素材時，由 cropInfo 算出來的原始素材尺寸；建立流程沒有素材、拿不到
  // cropInfo，所以維持 undefined。
  public splicedCreativeSize?: `${number} x ${number}`;
  public beaconConnectionOptions?: SelectOptions[];
  public channelGroupSetting: ChannelGroupSetting;

  constructor(
    public advertiserId: number,
    private channelGroupId: number,
    public creativeApprovalConfigs: CreativeApprovalConfig[],
    public forPmp: boolean = false,
    public canChangeBeacon: boolean = true,
    protected advertiserManager: AdvertiserManager = new DefaultAdvertiserManager(),
    protected channelGroupManager: ChannelGroupManager = new DefaultChannelGroupManager()
  ) {
    super();
    this.channelGroupSetting = ChannelGroupSetting.fromId(channelGroupId);
    this.validateImage = this.validateImage.bind(this);
    this.validateVideo = this.validateVideo.bind(this);
    this.validateAudio = this.validateAudio.bind(this);
    this.validateZip = this.validateZip.bind(this);
    this.validateDocument = this.validateDocument.bind(this);
    this.getOutdoorTypeOptions = this.getOutdoorTypeOptions.bind(this);
  }

  /**
   * 電子紙看板沒有喇叭也沒有 beacon 硬體，這兩個功能對它沒有意義（ECOPD-5492）。
   *
   * 選擇整個藏起來而不是留著讓人設定完才擋：設定了也投不出去，而且「圖+音」的固定
   * 時長會被音檔長度蓋掉（useMediaDuration），藏掉音檔等於把那條路一起斷乾淨。
   */
  get supportBeacon(): boolean {
    return !isEpaperChannelGroup(this.channelId);
  }

  get supportAudio(): boolean {
    return !isEpaperChannelGroup(this.channelId);
  }

  /**
   * 素材尺寸只給看、不給選。
   *
   * 電子紙的尺寸是渠道設定決定的（getCreativeSizes 只回一組寬高），下拉永遠只有一個
   * 選項，擺一個選單只會讓人以為有別的尺寸可挑。建立時就改用唯讀文字呈現，值照
   * getInitTypeProperties 帶進來的預設值送（ECOPD-5492）。
   */
  get fixedCreativeSize(): boolean {
    return isEpaperChannelGroup(this.channelId);
  }

  // 電子紙的素材不設生效時間：檔期由活動的四個固定時段決定，素材再壓一層時間窗只會
  // 讓「明明是啟用中卻沒在播」更難查（ECOPD-5492）。
  override get hideCreativeEnableTime(): boolean {
    return isEpaperChannelGroup(this.channelId);
  }

  get channelName() {
    return this.channelGroupSetting.config.name;
  }

  get sizeBoxingMap(): {
    [key: string]: { width: [number, number]; height: [number, number] };
  } {
    return {};
  }

  get channelId() {
    return this.channelGroupId;
  }

  get screenDisplayType() {
    return ScreenDisplayType.SINGLE;
  }

  /**
   * ECOPD-5722：連台素材存的是切割後的單格尺寸，編輯時要還原成切割前的整面牆，
   * 否則畫面顯示的尺寸跟使用者當初上傳的那支對不起來。非連台素材沒有 cropInfo，
   * 也不該套用。
   */
  protected getSplicedCreativeSize(creativeExtra: unknown) {
    return this.screenDisplayType === ScreenDisplayType.MULTI_SPLICED
      ? getCropInfoCreativeSizeText(creativeExtra)
      : undefined;
  }

  /**
   * ECOPD-5722：連台素材不給尺寸容差。合成時的切片寬 = 影片寬 ÷ 台數，差一個像素
   * 拼出來的合成檔就小於 RMax 設定的播放尺寸，後端也是精確比對 —— 這裡放行只會讓
   * 使用者填完整張表、送出之後才被擋。其他 outdoor 素材維持 1 px，那是給影片編碼的
   * 四捨五入誤差用的。
   */
  private get sizeTolerance(): number {
    return this.screenDisplayType === ScreenDisplayType.MULTI_SPLICED ? 0 : 1;
  }

  abstract get useMediaDuration(): boolean;

  // 是否允許「編輯」既有素材時仍可調整圖片播放時長。
  // 預設 false（編輯時鎖成唯讀）；僅研發測試用（Sandbox）頻道開放（見 RKB-2385 / ECOPD-5342）。
  get allowEditImageDuration(): boolean {
    return false;
  }

  // 「圖+音」素材的播放時長要在這個區間內由使用者自訂（分／秒下拉）；undefined 代表
  // 照原本的固定選項邏輯走。
  get durationRange(): { min: number; max?: number } | undefined {
    return undefined;
  }

  needSelfCheckDocument = (outdoorType: OutdoorType): boolean => {
    const config = this.creativeApprovalConfigs.find(
      (config) => config.outdoorType === outdoorType
    );
    if (!config) {
      return false;
    }
    return !config?.ignoreFields.includes('selfCheckList');
  };

  defaultSelfCheckDocLink = (outdoorType: OutdoorType): string | undefined => {
    const config = this.creativeApprovalConfigs.find(
      (config) => config.outdoorType === outdoorType
    );
    if (!config) {
      return;
    }
    return get(config, 'property.selfCheckLink');
  };

  abstract getFormContent(): (props: OutdoorFormProps<T>) => JSX.Element;

  abstract getDefaultDuration(
    newType: OutdoorType,
    oldDuration: number
  ): number;

  override async init() {
    if (!this.beaconConnectionOptions && this.supportBeacon) {
      this.beaconConnectionOptions = await this.getBeaconConnections();
    }
    if (!this.creativeSizes) {
      const sizes = await this.getCreativeSizes();
      this.creativeSizes = sizes
        ? sizes.map((size) => ({
            label: `${size.width} x ${size.height}`,
            value: `${size.width} x ${size.height}`
          }))
        : undefined;
    }
  }

  async getBeaconConnections() {
    try {
      return this.advertiserManager.getBeaconConnectionOptions(
        this.advertiserId
      );
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async getCreativeSizes(): Promise<CreativeSize[] | undefined> {
    try {
      return this.channelGroupManager.getOutdoorCreativeSizes(this.channelId);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  get neglectedFormFields(): string[] {
    return [];
  }

  get beaconTypeOptions(): SelectOptions[] {
    return createSelectOptionsFromEnum(
      BeaconType,
      'beaconType.',
      undefined,
      camelCase
    );
  }

  get initBeaconSwitch() {
    return false;
  }

  addLimitation?: (
    operate: string,
    limitationType: string,
    label: string,
    value: string
  ) => void;

  setLimitationHook(
    addLimitation: (
      operate: string,
      limitationType: string,
      label: string,
      value: string
    ) => void
  ): void {
    this.addLimitation = addLimitation;
  }

  getOutdoorTypeOptions() {
    return [
      {
        label: i18n.t('outdoorForm.labels.outdoorType1'),
        value: OutdoorType.VIDEO
      },
      {
        label: i18n.t('outdoorForm.labels.outdoorType2'),
        value: OutdoorType.IMAGE_AUDIO
      },
      {
        label: i18n.t('outdoorForm.labels.outdoorType4'),
        value: OutdoorType.HTML5
      }
    ];
  }

  getInitTypeProperties(): {
    creativeSize?: `${number} x ${number}`;
    outdoorType?: OutdoorType;
    duration?: number;
    useAudio: boolean;
  } {
    const creativeSizes = defaultTo(this.creativeSizes, []);
    return {
      creativeSize:
        creativeSizes.length > 0
          ? (creativeSizes[0].value.toString() as `${number} x ${number}`)
          : undefined,
      outdoorType: undefined,
      duration: 30,
      useAudio: false
    };
  }

  getCreativeSize(creativeBasic: CreativeBasic): `${number} x ${number}` {
    const creativeValueString = get(creativeBasic, 'creativeValues', '{}');
    const creativeValue = JSON.parse(creativeValueString);
    const videoWidth = get(creativeValue, 'videoW');
    const videoHeight = get(creativeValue, 'videoH');
    const imageWidth = get(creativeValue, 'imgW');
    const imageHeight = get(creativeValue, 'imgH');
    const htmlWidth = get(creativeValue, 'htmlW');
    const htmlHeight = get(creativeValue, 'htmlH');
    const creativeExtra = get(creativeBasic, 'bannerExtra', {});
    switch (creativeBasic.outdoorType) {
      case OutdoorType.VIDEO: {
        const splicedCreativeSize = this.getSplicedCreativeSize(creativeExtra);
        return (
          splicedCreativeSize ??
          this.getCreativeSizeByFileSize(videoWidth, videoHeight)
        );
      }
      case OutdoorType.VIDEO_IMAGE:
        return '1080 x 1920';
      case OutdoorType.IMAGE_AUDIO:
      case OutdoorType.IMAGE:
        return this.getCreativeSizeByFileSize(imageWidth, imageHeight);
      case OutdoorType.HTML5:
        return `${htmlWidth} x ${htmlHeight}`;
    }
    return '1080 x 1920';
  }

  getCreativeSizeByFileSize(
    width: number,
    height: number
  ): `${number} x ${number}` {
    const creativeSize = Object.keys(this.sizeBoxingMap).find(
      (creativeSize) => {
        const sizeBoxing = this.sizeBoxingMap[creativeSize];
        return (
          width >= sizeBoxing.width[0] &&
          width <= sizeBoxing.width[1] &&
          height >= sizeBoxing.height[0] &&
          height <= sizeBoxing.height[1]
        );
      }
    );
    if (!creativeSize) {
      return `${width} x ${height}`;
    }
    const widthHeight = creativeSize.split('x');
    return `${widthHeight[0]} x ${widthHeight[1]}` as `${number} x ${number}`;
  }

  getFormModelData(creative: Creative) {
    const creativeBasic: any = omit(creative.basic, [
      'id',
      'srcType',
      'creativeValues',
      'bannerExtra',
      'duration',
      'outdoorType'
    ]);
    const creativeExtra = get(creative, 'basic.bannerExtra', {});
    const creativeValueString = get(creative, 'basic.creativeValues', '{}');
    const creativeValue = JSON.parse(creativeValueString);
    const imageUrl = get(creativeValue, 'imageUrl');
    const videoUrl = get(creativeValue, 'videoUrl');
    const htmlUrl = get(creativeValue, 'htmlUrl');
    const audioUrl = get(creativeValue, 'audioUrl');
    const beaconConnectionId = get(creativeValue, 'beaconConnectionId');
    const beaconConnection = beaconConnectionId
      ? this.beaconConnectionOptions &&
        this.beaconConnectionOptions.find(
          (beaconConnection) =>
            beaconConnection.value.toString() === beaconConnectionId.toString()
        )
      : undefined;
    const duration = get(creativeValue, 'duration');
    const toUrlObject = (url) => (url ? { url, duration } : undefined);
    const finalVideoUrl = get(creativeExtra, 'originalVideoUrl', videoUrl);
    let mediaData =
      imageUrl || finalVideoUrl || htmlUrl || audioUrl
        ? omitBy(
            {
              image: toUrlObject(imageUrl),
              video: toUrlObject(finalVideoUrl),
              html: toUrlObject(htmlUrl),
              audio: toUrlObject(audioUrl)
            },
            isEmpty
          )
        : undefined;
    const useAudio = get(creativeExtra, 'useAudio', false);

    const lineMessages = get(creativeValue, 'lineMessage');
    const tenMaxBeaconMessage = get(creativeValue, 'tenMaxBeaconMessage');
    const intervalHours = get(creativeValue, 'intervalHours');
    const maxFrequency = get(creativeValue, 'maxFrequency');
    // TODO: 暫時只有 Line Beacon 會用到，如果未來有其他 Beacon 也會用到語系的話，再調整這邏輯
    const language = lineMessages
      ? get(creativeValue, 'language', LineUserLanguage.ALL)
      : undefined;
    const outdoorType = get(creative.basic, 'outdoorType', OutdoorType.VIDEO);
    let orderInfoValue = {};
    if (this.needSelfCheckDocument(outdoorType)) {
      const orderName = get(creativeExtra, 'orderName', '');
      const orderBudget = get(creativeExtra, 'orderBudget', '');
      const orderStartDate = get(
        creativeExtra,
        'startDate',
        moment().format('YYYY-MM-DD')
      );
      const orderEndDate = get(
        creativeExtra,
        'endDate',
        moment().add(1, 'day').format('YYYY-MM-DD')
      );
      const selfCheckList = get(creativeExtra, 'selfCheckList') as
        | string
        | undefined;
      orderInfoValue = {
        orderName,
        orderBudget,
        orderStartDate,
        orderEndDate
      };
      mediaData = {
        ...mediaData,
        selfCheckDocument: toUrlObject(selfCheckList)
      };
    }
    // 唯讀欄位會用 creativeSizes 這份選項驗值（useOutdoorBasicFields），而拼接尺寸
    // 不在渠道回的尺寸清單裡，所以要記回 model 讓選項補上，否則回填的值會驗不過。
    this.splicedCreativeSize = this.getSplicedCreativeSize(creativeExtra);
    return {
      basic: {
        ...creativeBasic,
        typeProperties: {
          outdoorType,
          duration: duration || get(creative.basic, 'duration', 30),
          useAudio,
          creativeSize: this.getCreativeSize(creative.basic),
          beaconConnection,
          intervalHours,
          maxFrequency,
          language,
          ...orderInfoValue
        },
        medias: mediaData,
        tenMaxBeaconMessage,
        lineMessage: lineMessages
          ? lineMessages.map((lineMessage: LineMessage) => ({
              type: lineMessage.type,
              message:
                lineMessage.type === LineMessageType.TEXT
                  ? lineMessage.message
                  : undefined,
              image:
                lineMessage.type === LineMessageType.IMAGE
                  ? {
                      url: lineMessage.imageUrl
                    }
                  : undefined,
              video:
                lineMessage.type === LineMessageType.VIDEO
                  ? {
                      url: lineMessage.videoUrl
                    }
                  : undefined,
              previewImage:
                lineMessage.type === LineMessageType.VIDEO
                  ? {
                      url: lineMessage.previewImageUrl
                    }
                  : undefined,
              officialAccount:
                lineMessage.type === LineMessageType.JOIN
                  ? lineMessage.officialAccount
                  : undefined,
              couponId:
                lineMessage.type === LineMessageType.COUPON
                  ? lineMessage.couponId
                  : undefined
            }))
          : undefined
      },
      limitations: creative.limitations
    };
  }

  getVideoHints(width: number, height: number) {
    // ECOPD-5471: 由渠道設定驅動的「支援影片編碼」提示（例如 outdoor 支援 H264/H265）。
    // allowedVideoCodecs 由後端 channelGroup API 頂層欄位帶入；為空時不顯示此提示。
    const allowedVideoCodecs =
      this.channelGroupSetting.config.allowedVideoCodecs;
    const videoCodecHint = !isEmpty(allowedVideoCodecs)
      ? i18n.t('creativeSetupFlow.labels.videoCodecHint', {
          codecs: defaultTo(allowedVideoCodecs, []).join(', ')
        })
      : undefined;
    return compact([
      i18n.t('creativeSetupFlow.labels.videoTypeHint'),
      videoCodecHint,
      isEmpty(this.sizeBoxingMap)
        ? i18n.t('outdoorForm.labels.videoSizeHint', {
            size: `${width} x ${height}`
          })
        : i18n.t('outdoorForm.labels.videoSizeBoxingHint'),
      this.screenDisplayType === ScreenDisplayType.SINGLE
        ? i18n.t('creativeSetupFlow.labels.storageHint', {
            storage: '490MB'
          })
        : undefined
    ]);
  }

  getImageHints(width: number, height: number) {
    return compact([
      i18n.t('creativeSetupFlow.labels.typeHint'),
      isNil(this.creativeSizes)
        ? undefined
        : isEmpty(this.sizeBoxingMap)
          ? i18n.t('creativeSetupFlow.labels.imageSizeHint', {
              size: `${width} x ${height}`
            })
          : i18n.t('outdoorForm.labels.imageSizeBoxingHint'),
      this.screenDisplayType === ScreenDisplayType.SINGLE
        ? i18n.t('creativeSetupFlow.labels.storageHint', {
            storage: '2MB'
          })
        : undefined
    ]);
  }

  getAudioHints() {
    return [
      i18n.t('creativeSetupFlow.labels.audioTypeHint'),
      i18n.t('creativeSetupFlow.labels.storageHint', { storage: '2MB' })
    ];
  }

  isFileFieldEmpty(
    fileData: FileFieldData | EmptyFileFieldData
  ): fileData is EmptyFileFieldData {
    return !fileData || (!fileData.file && !fileData.url);
  }

  hasFile(fileData: FileFieldData | EmptyFileFieldData): fileData is {
    file: File;
    url?: string;
  } {
    return !!fileData.file;
  }

  validateImage(
    fileData: ImageFieldData,
    targetWidth: number,
    targetHeight: number
  ): string | JSX.Element | undefined;
  validateImage(
    fileData: ImageFieldData,
    targetWidth?: undefined,
    targetHeight?: undefined
  ): string | JSX.Element | undefined;
  validateImage(
    fileData: ImageFieldData,
    targetWidth?: number,
    targetHeight?: number
  ) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (this.isFileFieldEmpty(fileData)) {
      return i18n.t('formValidate.labels.emptyError');
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    const file = fileData.file;
    if (validTypes.indexOf(file.type) === -1) {
      const extensions = get(mimeDB[file.type], 'extensions', ['Unknown']);
      return i18n.t('creativeSetupFlow.labels.typeErrorHint', {
        type: extensions[0]
      });
    }

    const errors: any[] = [];
    if (targetWidth !== undefined && targetHeight !== undefined) {
      const width = fileData.width;
      const height = fileData.height;
      const targetSizeKey = `${targetWidth}x${targetHeight}`;
      const allowedImageSizeBoxing = this.sizeBoxingMap[targetSizeKey];
      if (allowedImageSizeBoxing) {
        const allowedWidthRange = this.sizeBoxingMap[targetSizeKey].width;
        const allowedHeightRange = this.sizeBoxingMap[targetSizeKey].height;
        if (
          width < allowedWidthRange[0] ||
          width > allowedWidthRange[1] ||
          height < allowedHeightRange[0] ||
          height > allowedHeightRange[1]
        ) {
          errors.push(
            i18n.t('creativeSetupFlow.labels.outdoorImageSizeErrorHint', {
              size: `${width} x ${height}`
            })
          );
        }
      } else if (
        Math.abs(width - targetWidth) > this.sizeTolerance ||
        Math.abs(height - targetHeight) > this.sizeTolerance
      ) {
        errors.push(
          i18n.t('creativeSetupFlow.labels.sizeErrorHint2', {
            size1: `${width} x ${height}`,
            size2: `${targetWidth} x ${targetHeight}`
          })
        );
      }
    }
    if (
      this.screenDisplayType === ScreenDisplayType.SINGLE &&
      file.size > 2097152
    ) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '2MB'
        })
      );
    }

    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  validateVideo(
    targetWidth: number,
    targetHeight: number,
    fileData: MediaFieldData
  ) {
    const validTypes = ['video/mp4'];
    if (this.isFileFieldEmpty(fileData)) {
      return i18n.t('formValidate.labels.emptyError');
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    const file = fileData.file;
    if (validTypes.indexOf(file.type) === -1) {
      return i18n.t('creativeSetupFlow.labels.videoTypeHint');
    }

    const errors: any[] = [];
    const width = fileData.width;
    const height = fileData.height;
    // POS-7999：H.265 影片在瀏覽器多半無法解碼，前端拿不到寬高（width/height 為空）。
    // 此時略過前端尺寸驗證，改由後端以實際分析結果把關（後端亦以 codec 白名單決定可用渠道）。
    const metadataAvailable = width != null && height != null;
    const targetSizeKey = `${targetWidth}x${targetHeight}`;
    const allowedVideoSizeBoxing = this.sizeBoxingMap[targetSizeKey];
    if (metadataAvailable && allowedVideoSizeBoxing) {
      const allowedWidthRange = this.sizeBoxingMap[targetSizeKey].width;
      const allowedHeightRange = this.sizeBoxingMap[targetSizeKey].height;
      if (
        width < allowedWidthRange[0] ||
        width > allowedWidthRange[1] ||
        height < allowedHeightRange[0] ||
        height > allowedHeightRange[1]
      ) {
        errors.push(
          i18n.t('creativeSetupFlow.labels.outdoorVideoSizeErrorHint', {
            size: `${width} x ${height}`
          })
        );
      }
    } else if (metadataAvailable) {
      const matchesTargetSize =
        Math.abs(width - targetWidth) <= this.sizeTolerance &&
        Math.abs(height - targetHeight) <= this.sizeTolerance;
      if (!matchesTargetSize) {
        errors.push(
          i18n.t('outdoorForm.labels.videoSizeHint', {
            size: `${targetWidth} x ${targetHeight}`
          })
        );
      }
    }

    if (
      this.screenDisplayType === ScreenDisplayType.SINGLE &&
      file.size > 513802240
    ) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '490MB'
        })
      );
    }
    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  validateAudio(fileData: MediaFieldData) {
    if (this.isFileFieldEmpty(fileData)) {
      return i18n.t('formValidate.labels.emptyError');
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    const validTypes = ['audio/mpeg'];
    const file = fileData.file;
    if (validTypes.indexOf(file.type) === -1) {
      const extensions = get(mimeDB[file.type], 'extensions', ['Unknown']);
      return i18n.t('creativeSetupFlow.labels.typeErrorHint', {
        type: extensions[0]
      });
    }

    const errors: any[] = [];
    if (file.size > 2097152) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '2MB'
        })
      );
    }

    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  async validateZip(fileData: FileFieldData) {
    if (this.isFileFieldEmpty(fileData)) {
      return i18n.t('formValidate.labels.emptyError');
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    const validTypes = ['application/zip', 'application/x-zip-compressed'];
    const file = fileData.file;
    if (validTypes.indexOf(file.type) === -1) {
      const extensions = get(mimeDB[file.type], 'extensions', ['Unknown']);
      return i18n.t('html5Form.labels.typeErrorHint', {
        type: extensions[0]
      });
    }

    const errors: any[] = [];
    if (file.size > 2097152) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '2MB'
        })
      );
    }

    const zipData = await JSZip.loadAsync(file);
    const contents: string[] = [];
    zipData.forEach((_1, zipEntry) => {
      contents.push(zipEntry.name);
    });
    if (!contents.includes('index.html')) {
      errors.push(i18n.t('creativeSetupFlow.labels.zipContentError'));
    }

    return errors.length > 0 ? renderErrors(errors) : undefined;
  }

  validateDocument(
    fileData: FileFieldData,
    isExpressPass: boolean
  ): string | JSX.Element | undefined {
    if (this.isFileFieldEmpty(fileData)) {
      return isExpressPass
        ? undefined
        : i18n.t('formValidate.labels.emptyError');
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    let errors: string[] = [];
    const file = fileData.file;
    const validTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (validTypes.indexOf(file.type) === -1) {
      const extensions = get(mimeDB[file.type], 'extensions', ['Unknown']);
      return i18n.t('formikConfigInputField.errors.fileType', {
        type: extensions[0],
        types: 'doc, docx'
      });
    }
    if (file.size > 5242880) {
      errors.push(
        i18n.t('creativeSetupFlow.labels.storageErrorHint', {
          storage1: formatBytes(file.size),
          storage2: '5MB'
        })
      );
    }
    return errors.length > 0 ? renderErrors(errors) : undefined;
  }
}
